import * as globals from '@wandb/common/css/globals.styles';
import {WBIcon} from '@wandb/ui';
import {Button, Dropdown, Form, Image, Input} from 'semantic-ui-react';
import styled, {css} from 'styled-components';

const SMALL_DESKTOP_WIDTH_PX = 1300;
const MOBILE_WIDTH_PX = 880;
const PAGE_PADDING_PX = 32;
const DIALOG_SPACING_PX = 32;

export const Page = styled.div`
  position: relative;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 100px;
  background: linear-gradient(
    287.64deg,
    ${globals.gray800} 0%,
    ${globals.gray900} 100%
  );
  overflow-x: hidden;
  overflow-y: auto;

  @media (max-width: ${SMALL_DESKTOP_WIDTH_PX}px) {
    padding: 64px 36px 36px 36px;
  }
  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    justify-content: flex-start;
    padding: 0 16px;
  }
`;

export const PageContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 1260px;

  @media (max-width: ${SMALL_DESKTOP_WIDTH_PX}px) {
    max-width: 960px;
  }
  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

export const BackgroundCurves = styled.img`
  position: absolute;
  left: 0;
  width: 100%;

  @media (min-width: ${MOBILE_WIDTH_PX + 1}px) {
    bottom: 0;
  }
  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    top: -3px;
    min-width: 960px;
    transform: rotateX(180deg);
  }
`;

export const FlavorSection = styled.div`
  position: relative;
  z-index: 1;

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    margin: 16px 0;
  }
`;

export const Logo = styled.img`
  width: 40px;
  height: 40px;
  display: block;

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    width: 25px;
    height: 25px;
  }
`;

export const FlavorHeader = styled.h1`
  color: ${globals.white};
  font-family: 'Source Serif Pro';
  font-size: 60px;
  line-height: 68px;

  @media (max-width: ${SMALL_DESKTOP_WIDTH_PX}px) {
    font-size: 40px;
    line-height: 48px;
  }

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    font-size: 30px;
    line-height: 38px;
    margin: 16px 0 4px;
  }
`;

export const FlavorHeaderColored = styled.span`
  color: ${globals.gold};
`;

export const FlavorText = styled.div`
  color: ${globals.gray400};
  font-size: 24px;
  line-height: 30px;

  @media (max-width: ${SMALL_DESKTOP_WIDTH_PX}px) {
    font-size: 20px;
    line-height: 25px;
  }

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export const Dialog = styled.div`
  position: relative;
  z-index: 1;
  background-color: ${globals.white};
  border-radius: 5px;

  @media (min-width: ${SMALL_DESKTOP_WIDTH_PX + 1}px) {
    width: 512px;
    padding: ${PAGE_PADDING_PX}px;
  }
  @media (max-width: ${SMALL_DESKTOP_WIDTH_PX}px) {
    width: 400px;
    padding: 32px;
  }
  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    width: 100%;
    padding: 24px;
    margin-bottom: 16px;
  }
`;

export const ProgressBarContainer = styled.div`
  background-color: ${globals.primaryBackground};
  height: 8px;
  border-radius: 8px;
  overflow: hidden;
`;

export const ProgressBar = styled.div<{percentWidth: number}>`
  background-color: ${globals.primary};
  height: 100%;
  width: ${p => p.percentWidth}%;
  transition: width 0.5s;
  border-radius: 8px;
`;

export const DialogHeader = styled.h2<{smallBottomMargin?: boolean}>`
  color: ${globals.gray800};
  margin: ${DIALOG_SPACING_PX}px 0 ${p => (p.smallBottomMargin ? 12 : 24)}px;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    font-size: 20px;
    line-height: 25px;
    margin: 24px 0 16px;
  }
`;

export const FormRow = styled.div`
  position: relative;
  margin: 24px 0;

  @media (max-width: ${MOBILE_WIDTH_PX}px) {
    margin: 16px 0;
  }
`;

export const FormInput = styled(Form.Field)<{
  $prefixed?: boolean;
  $error?: boolean;
}>`
  &&&&& {
    margin: 0;

    label {
      font-size: 13px;
      font-weight: 600;
      line-height: 16px;
      margin-bottom: 2px;
      &:after {
        color: inherit;
        margin-top: 0;
      }
    }

    input[type='text'] {
      border: 1px solid ${globals.gray400};
      border-radius: 5px;
      box-shadow: none;
      padding-left: 7px;
      padding-right: 7px;
    }

    ${p =>
      p.$prefixed &&
      css`
        input[type='text'] {
          padding-left: 75px;
        }
      `}

    ${p =>
      p.$error &&
      css`
        input[type='text'] {
          border-color: ${globals.error};
        }
      `}
  }
`;

export const UsernamePrefix = styled.div`
  position: absolute;
  z-index: 1;
  top: 25px;
  left: 8px;
`;

export const FormHint = styled.div<{error?: boolean}>`
  margin-top: 2px;
  color: ${p => (p.error ? globals.errorText : globals.gray600)};
  font-size: 12px;
  line-height: 15px;
`;

export const SingleDialogButton = styled(Button)`
  display: block;
  width: 100%;
`;

export const DialogButtons = styled.div`
  display: flex;
  margin-top: 32px;
`;

export const DialogButton = styled(Button)`
  &&&&& {
    flex-grow: 1;
    margin-right: 8px;
  }
`;

export const DialogSkipButton = styled(Button)`
  &&&&& {
    width: 107px;
    border: none;
    color: ${globals.primary};
    background-color: ${globals.white};
  }
`;

export const FormChoicesContainer = styled.div<{checkbox?: boolean}>`
  &&& {
    margin: ${DIALOG_SPACING_PX}px 0;

    .field {
      display: flex;
      align-items: center;

      &:not(.select-all) {
        &:not(.checked) {
          background-color: ${globals.gray50};
        }
        &.checked {
          background-color: ${globals.primaryBackground};
        }
      }
      &.select-all {
        height: 18px;
      }

      &:not(:first-child) {
        margin-top: 8px;
      }

      .checkbox {
        width: 100%;
      }

      label {
        color: ${globals.black};
        padding: 12px 16px 11px 42px;
      }

      // radio button circle or checkbox box
      label:before {
        top: ${p => (p.checkbox ? 0 : 1)}px;
        left: 16px;
        bottom: 0;
        margin: auto;
        width: 18px;
        height: 18px;
        border-color: ${globals.gray600};
        ${p =>
          p.checkbox &&
          css`
            border-radius: 2px;
          `}
      }

      // checkmark
      label:after {
        top: ${p => (p.checkbox ? 0 : 1)}px;
        left: 16px;
        bottom: 0;
        margin: auto;
        width: 18px;
        height: 18px;
        ${p =>
          p.checkbox &&
          css`
            display: flex;
            justify-content: center;
            align-items: center;
            color: ${globals.white};
            background-color: ${globals.primary};
            border-radius: 2px;
          `}
      }
    }

    @media (max-width: ${MOBILE_WIDTH_PX}px) {
      margin: 16px 0 32px;
    }
  }
`;

export const OtherInput = styled(Input)`
  margin-top: 8px;
  width: 100%;
`;

export const NavDropdown = styled(Dropdown)`
  &&&&& {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;

    i.dropdown.icon {
      display: none;
    }
  }
`;

export const NavDropdownMenu = styled(Dropdown.Menu)`
  &&&&& {
    background-color: ${globals.gray800};
  }
`;

export const NavDropdownItem = styled(Dropdown.Item)`
  &&&&& {
    color: ${globals.white};
  }
`;

export const NavDropdownDivider = styled(Dropdown.Divider)`
  &&&&& {
    border-color: ${globals.white};
    opacity: 0.5;
    margin: 0;
  }
`;

export const Avatar = styled(Image)`
  &&&&& {
    margin: 0;
  }
`;

export const Checklist = styled.div``;

export const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  line-height: 20px;

  &:not(:first-child) {
    margin-top: 6px;
  }
`;

export const ChecklistIcon = styled(WBIcon)`
  position: relative;
  top: -1px;
  color: ${globals.success};
  margin-right: 6px;
  font-size: 22px;
`;

export const InviteTeammatesTextWrapper = styled.div`
  font-size ${globals.fontSizeStandard};
  color: ${globals.gray800};
  line-height: 20px;
`;

export const EmphasizedText = styled.span`
  font-weight: 600;
`;
